import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { scenariosApi } from '../../api/scenarios';

export default function DuplicateScenarioDialog({ 
  open, 
  onClose, 
  onSuccess, 
  scenario,
  partNumber,
  programId
}) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: `Copy of ${scenario?.name || ''}`,
    description: ''
  });
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await scenariosApi.duplicateScenario(partNumber, scenario.id, formData, { programId });

      if (response.success) {
        onSuccess(response.data);
        // Reset form
        setFormData({ name: `Copy of ${scenario.name}`, description: '' });
        onClose();
      } else {
        setError(response.error || 'Failed to duplicate scenario');
      }
    } catch (err) {
      setError(err.message || 'Failed to duplicate scenario');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: `Copy of ${scenario?.name || ''}`, description: '' });
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Duplicate Scenario</DialogTitle>
          <DialogDescription>
            Create a copy of "{scenario?.name}" with all forecast and cost data
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* New Scenario Name */}
          <div className="space-y-2">
            <Label htmlFor="name">New Scenario Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Copy of Mexico Alternative"
              required
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description (Optional)</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe what makes this copy different..."
              rows={3}
            />
          </div>

          <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm">
            <strong>Note:</strong> This will copy all forecast data and price components from "{scenario?.name}". 
            Actual values will not be copied.
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !formData.name}>
              {loading ? 'Duplicating...' : 'Duplicate Scenario'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
